<template>
  <div class="congruence-similarity-viz">
    <div class="viz-header-compact" >
      <div class="brief" v-latex="knowledgePoint"></div>
      <div class="formula-chip" v-latex> \$2\$ </div>
    </div>

    <div class="main-layout vertical">
      <div class="simulation-stage dark-stage">
        <!-- Euclidean Algorithm Geometric Representation (Tiling) -->
        <svg width="100%" height="100%" class="canvas-svg">
          <g :transform="`translate(${(svgWidth - scale*Math.max(a, b))/2}, ${(svgHeight - scale*Math.min(a, b))/2 + 10})`">
            <!-- Outline -->
            <rect :width="scale * Math.max(a, b)" :height="scale * Math.min(a, b)" fill="none" stroke="#334155" stroke-width="2" />
            
            <g v-for="(step, idx) in steps" :key="'grp'+idx">
               <!-- Draw squares for this step -->
               <g v-for="sq in step.squares" :key="'sq'+sq.id">
                  <rect :x="sq.x * scale" :y="sq.y * scale" 
                        :width="sq.size * scale" :height="sq.size * scale" 
                        :fill="colors[idx % colors.length]" stroke="#1e293b" stroke-width="1.5" />
                  <!-- Show text only if square is large enough -->
                  <text v-if="sq.size * scale > 18" 
                        :x="(sq.x + sq.size/2)*scale" :y="(sq.y + sq.size/2)*scale + (sq.size*scale>30 ? 6 : 4)" 
                        fill="#0f172a" 
                        font-weight="900" 
                        :font-size="sq.size * scale > 40 ? 18 : 12" 
                        text-anchor="middle" opacity="0.6">
                     {{ sq.size }}
                  </text>
               </g>
            </g>
          </g>
        </svg>

        <div class="hud-center">
          <div class="status-badge con">
             最大公因数 GCD({{ Math.max(a,b) }}, {{ Math.min(a,b) }}) = {{ gcdResult }}
          </div>
        </div>
      </div>

      <div class="compact-controls">
         <div class="control-card glass">
            <div class="control-vertical-group">
               <div class="param-row">
                  <div class="s-item" style="grid-column: span 2;">
                     <label>数值 A (长)</label>
                     <el-slider v-model="a" :min="1" :max="400" input-size="mini"></el-slider>
                  </div>
                  <div class="s-item" style="grid-column: span 2;">
                     <label>数值 B (宽)</label>
                     <el-slider v-model="b" :min="1" :max="400" input-size="mini"></el-slider>
                  </div>
               </div>
            </div>
         </div>
         <div class="discovery-card-compact" v-latex>
            <i>🔍</i>
            <p><strong>几何与代数交织之美：</strong>如果长 A 被宽 B “整除”，则只需铺一次正方形。<br/>如果有余数（带余除法：$A = qB + R$），则剩下 $B \times R$ 的矩形缝隙。<br/>“辗转相除法”等价于：不断用正方形铺满余下的矩形缝隙，直到完全无缝填满。最后铺装的这块<strong>“最小正方形”的边长</strong>，就是 A 和 B 的<strong>最大公因数</strong>！</p>
         </div>
      </div>
    </div>
  </div>
</template>

<script>
export default { props: { knowledgePoint: String },
  name: 'EuclideanGCDViz',
  data() {
    return {
      a: 192,
      b: 72,
      svgWidth: 800,
      svgHeight: 300,
      colors: ['rgba(56, 189, 248, 0.95)', 'rgba(42, 245, 152, 0.95)', 'rgba(250, 204, 21, 0.95)', 'rgba(244, 63, 94, 0.95)', 'rgba(168, 85, 247, 0.95)', 'rgba(249, 115, 22, 0.95)']
    }
  },
  mounted() {
    // Attempt to responsive sizing if needed
    let el = this.$el.querySelector('.canvas-svg');
    if(el && el.clientWidth) {
      this.svgWidth = el.clientWidth;
      this.svgHeight = el.clientHeight || 300;
    }
  },
  computed: {
    scale() {
       let mWidth = Math.max(this.a, this.b);
       let mHeight = Math.min(this.a, this.b);
       // Fit in width/height dynamically
       return Math.min((this.svgWidth-40) / Math.max(1, mWidth), (this.svgHeight-40) / Math.max(1, mHeight));
    },
    gcdResult() {
       let calcGcd = (x, y) => (!y ? x : calcGcd(y, x % y));
       return calcGcd(this.a, this.b);
    },
    steps() {
       let stepsArr = [];
       let w = Math.max(this.a, this.b);
       let h = Math.min(this.a, this.b);
       if (h === 0) return [];
       
       let xOffset = 0;
       let yOffset = 0;
       let id = 0;
       let dir = 0; // 0 for horiz, 1 for vert
       
       let remW = w;
       let remH = h;
       
       // Max iterations to prevent infinite loop just in case
       let maxIter = 1000;
       while (remW >= 0.1 && remH >= 0.1 && maxIter > 0) {
          maxIter--;
          if (dir === 0) {
             let size = remH;
             let count = Math.floor(remW / remH);
             if(count === 0) break;
             let stepSq = [];
             for(let i=0; i<count; i++) {
                stepSq.push({x: xOffset + i*size, y: yOffset, size: size, id: id++});
             }
             stepsArr.push({squares: stepSq});
             xOffset += count * size;
             remW -= count * size;
             dir = 1;
          } else {
             let size = remW;
             let count = Math.floor(remH / remW);
             if(count === 0) break;
             let stepSq = [];
             for(let i=0; i<count; i++) {
                stepSq.push({x: xOffset, y: yOffset + i*size, size: size, id: id++});
             }
             stepsArr.push({squares: stepSq});
             yOffset += count * size;
             remH -= count * size;
             dir = 0;
          }
       }
       return stepsArr;
    }
  }
}
</script>

<style scoped lang="scss">
.congruence-similarity-viz { flex: 1; display: flex; flex-direction: column; gap: 8px; height: 100%; overflow: hidden; }
.viz-header-compact { display: flex; justify-content: space-between; align-items: center; padding: 0 4px; .brief { font-size: 13px; color: #1e293b; font-weight: 500; } .formula-chip { background: #f0fdf4; color: #10b981; border: 1px solid #bbf7d0; padding: 4px 14px; border-radius: 20px; font-weight: 700; font-family: monospace; } }
.main-layout { display: flex; gap: 12px; height: 100%; min-height: 0; flex-direction: column; }
.simulation-stage { flex: 1; min-height: 200px; background: #0f172a; border-radius: 12px; border: 1.5px solid #1e293b; position: relative; overflow: hidden; .canvas-svg { width: 100%; height: 100%; } .hud-center { position: absolute; top: 15px; width: 100%; text-align: center; display: flex; justify-content: center; .status-badge { background: rgba(255,255,255,0.1); border: 1px solid rgba(255,255,255,0.2); padding: 4px 16px; border-radius: 20px; color: #fff; font-weight: bold; font-size: 13px; backdrop-filter: blur(4px); &.con { background: rgba(16,185,129,0.2); border-color: #10b981; color: #6ee7b7; } } } }
.compact-controls { display: flex; flex-direction: column; gap: 8px; }
.control-card { background: #ffffff; padding: 12px 16px; border-radius: 12px; border: 1px solid #e2e8f0; }
.control-vertical-group { display: flex; flex-direction: column; gap: 10px; .param-row { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; .s-item { label { font-size: 11px; color: #64748b; font-weight: 700; display: block; margin-bottom: -5px; } } } }
.discovery-card-compact { background: #f8fafc; padding: 10px 14px; border-radius: 10px; border: 1px solid #e2e8f0; display: flex; align-items: center; gap: 10px; i { font-style: normal; font-size: 1.2rem; } p { flex: 1; margin: 0; font-size: 12px; color: #475569; line-height: 1.5; strong { color: #1e293b; } } }
</style>
